// The accounts a staffer has open as tabs in the sidebar.
//
// An account used to live in Customer Lookup's local state, so leaving for
// Tickets or the Dashboard threw it away and coming back meant searching for
// it again — and two accounts at once meant doing that on every switch. Now an
// opened account stays in the sidebar under "Open accounts" until it is closed.
//
// Same storage trade as lib/accountSession: sessionStorage, so the list
// survives a reload but not the browser tab, and never outlives the unlock
// tokens it depends on.
//
// The OTP consent boundary is enforced HERE, not by each caller: openAccount()
// refuses an account that has not been unlocked in this tab. A new surface that
// opens accounts cannot put a locked one in the list by forgetting the check.

import { isUnlocked } from './accountSession.js';

const KEY = 'squish.crm.openAccounts';

// Every open account stays mounted so switching is instant and keeps its own
// sub-tab and scroll; this caps how many of those are alive at once. Opening
// one more drops the longest-open.
export const MAX_OPEN = 8;

// Each open account wears its own colour — sidebar tab, account page, the
// "is this the right account?" check — so two customers side by side never
// look alike. The colour is a slot number held on the entry, taken when the
// account opens: the lowest slot no other open account holds. Closing one
// frees its slot and nobody else's colour moves. App.css paints the first
// ACCOUNT_PALETTE_SIZE slots (--acct-0 …), tuned per theme; MAX_OPEN fits
// inside that, and accountColor() still answers past it should the cap rise.
export const ACCOUNT_PALETTE_SIZE = 8;

/** The CSS colour for a slot. */
export function accountColor(slot) {
  const n = Number.isInteger(slot) && slot >= 0 ? slot : 0;
  if (n < ACCOUNT_PALETTE_SIZE) return `var(--acct-${n})`;
  // Golden-angle hues: each further slot lands far from every earlier one.
  return `hsl(${Math.round((n * 137.508) % 360)} 70% 62%)`;
}

function freeSlot(list) {
  const taken = new Set(list.map(e => e.color));
  let slot = 0;
  while (taken.has(slot)) slot++;
  return slot;
}

function clean(entry) {
  if (!entry || typeof entry.id !== 'string' || !entry.id) return null;
  return {
    id: entry.id,
    name: typeof entry.name === 'string' && entry.name ? entry.name : null,
    number: typeof entry.number === 'string' && entry.number ? entry.number : null,
    color: Number.isInteger(entry.color) && entry.color >= 0 ? entry.color : null,
  };
}

function read() {
  let list;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY) || '[]');
    list = Array.isArray(parsed) ? parsed.map(clean).filter(Boolean) : [];
  } catch {
    return [];
  }
  // A list saved before colours existed, or two entries claiming one slot:
  // the first holder keeps it and the rest take free ones, in opening order,
  // so every read of the same storage paints the same colours.
  const seen = new Set();
  for (const e of list) {
    if (e.color === null || seen.has(e.color)) e.color = null;
    else seen.add(e.color);
  }
  for (const e of list) {
    if (e.color === null) { e.color = freeSlot(list); seen.add(e.color); }
  }
  return list;
}

function write(list) {
  try { sessionStorage.setItem(KEY, JSON.stringify(list)); } catch { /* see accountSession write() */ }
  notify();
}

// Only fields with a value overwrite: a Tickets click knows the id alone, and
// must not blank the name a search click already supplied.
function merge(entry, meta = {}) {
  return {
    ...entry,
    ...(meta.name ? { name: String(meta.name) } : {}),
    ...(meta.number ? { number: String(meta.number) } : {}),
  };
}

/** The open accounts, in the order they were opened. */
export function listOpenAccounts() {
  return read();
}

/**
 * Add an account to the open list (or refresh its name/number if already
 * there). Returns false, changing nothing, if the account is not unlocked.
 */
export function openAccount(id, meta = {}) {
  if (!id || !isUnlocked(id)) return false;
  const list = read();
  const idx = list.findIndex(e => e.id === id);
  if (idx !== -1) {
    const next = merge(list[idx], meta);
    // Every in-app switch re-opens the account via the hashchange listener;
    // nothing changed, so nothing to write or re-render.
    if (next.name === list[idx].name && next.number === list[idx].number) return true;
    list[idx] = next;
  } else {
    while (list.length >= MAX_OPEN) list.shift();
    list.push(merge({ id, name: null, number: null, color: freeSlot(list) }, meta));
  }
  write(list);
  return true;
}

/** Fill in the name/number once the record has loaded. No-op if not open. */
export function updateOpenAccount(id, meta) {
  const list = read();
  const idx = list.findIndex(e => e.id === id);
  if (idx === -1) return;
  const next = merge(list[idx], meta);
  if (next.name === list[idx].name && next.number === list[idx].number) return;
  list[idx] = next;
  write(list);
}

/** Remove one account from the list. Its unlock is kept: reopening is free. */
export function closeAccount(id) {
  const list = read();
  const next = list.filter(e => e.id !== id);
  if (next.length !== list.length) write(next);
}

// ─── Change notification ─────────────────────────────────────────────────────

const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(); } catch (e) { console.warn('[openAccounts] listener failed:', e); }
  }
}

export function subscribeOpenAccounts(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
